// Libs
import classNames from 'classnames/bind';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid2, Stack, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseAutocomplete, BaseInput } from '~/components/common';
// Others
import { RoleEnum, TypeCrmEnum } from '~/utils/enum/common';
import { getListUser } from '~/thunks/user/userThunk';
import { getFullName } from '~/utils/helper/common';
import { IFormClient } from '~/utils/interface/client';
import { IBaseOption } from '~/utils/interface/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IQueryParamUser } from '~/utils/interface/user';
import { DEFAULT_CURRENT_PAGE, DEFAULT_MAX_PAGE, EMPTY_STRING } from '~/utils/constants/common';
// Styles, images, icons
import styles from './BusinessSettingsClientForm.module.scss';

type Props = {
  typeList: string;
};

const cx = classNames.bind(styles);

const BusinessSettingsClientForm = (props: Props) => {
  //#region Destructuring Props
  const { typeList } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/client', 'common/base']);
  const dispatch = useAppDispatch();

  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<IFormClient>();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [optionUser, setOptionUser] = useState<IBaseOption[]>([]);
  const [optionUserAssign, setOptionUserAssign] = useState<IBaseOption[]>([]);
  const [isLoadingUser, setIsLoadingUser] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    const paramObject: IQueryParamUser = {
      page: DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_MAX_PAGE,
      role: RoleEnum.SALES_PERSON,
    };

    handleGetListUser(paramObject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const paramObject: IQueryParamUser = {
      page: DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_MAX_PAGE,
    };

    handleGetListAssignUser(paramObject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListUser = (params: IQueryParamUser) => {
    setIsLoadingUser(true);

    dispatch(getListUser(params))
      .unwrap()
      .then((res) => {
        const { responses: listClient } = res?.data;

        if (!listClient) return;

        const convertOptionClient: IBaseOption[] = listClient.map((item) => ({
          value: item.id,
          label: getFullName({ firstName: item.firstName, lastName: item.lastName }),
        }));

        setOptionUser(convertOptionClient);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoadingUser(false);
      });
  };

  const handleGetListAssignUser = (params: IQueryParamUser) => {
    setIsLoadingUser(true);

    dispatch(getListUser(params))
      .unwrap()
      .then((res) => {
        const { responses: listClient } = res?.data;

        if (!listClient) return;

        const convertOptionClient: IBaseOption[] = listClient.map((item) => ({
          value: item.id,
          label: getFullName({ firstName: item.firstName, lastName: item.lastName }),
        }));

        setOptionUserAssign(convertOptionClient);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoadingUser(false);
      });
  };

  const handleChangeUser = (option: IBaseOption | IBaseOption[] | null) => {
    const data = option as IBaseOption;

    setValue('assignedAccountId', data.value);
  };

  const handleChangeSalePerson = (option: IBaseOption | IBaseOption[] | null) => {
    const data = option as IBaseOption;

    setValue('salesAccountId', data.value);
  };
  //#endregion Handle Function

  return (
    <Stack id='businessSettingsClientFormComponent' gap={4}>
      {typeList === TypeCrmEnum.CLIENT && (
        <Stack gap={'14px'}>
          <Typography className={cx('title')}>{t('client_form_title_business_settings')}</Typography>

          <Grid2 container rowSpacing={3} columnSpacing={5}>
            <Grid2 size={6}>
              <Controller
                control={control}
                name={'assignedAccountId'}
                render={({ field: { value, onChange } }) => {
                  return (
                    <BaseAutocomplete
                      height={36}
                      options={optionUserAssign}
                      textSearch={optionUserAssign.find((item) => item.value === value)?.label || EMPTY_STRING}
                      onTextSearchChange={onChange}
                      onChange={handleChangeUser}
                      label={t('client_form_title_assigned_user')}
                      placeholder={t('client_form_title_assigned_user_search')}
                      isLoading={isLoadingUser}
                    />
                  );
                }}
              />
            </Grid2>

            <Grid2 size={6}>
              <Controller
                control={control}
                name={'salesAccountId'}
                render={({ field: { value, onChange } }) => {
                  return (
                    <BaseAutocomplete
                      height={36}
                      options={optionUser}
                      textSearch={optionUser.find((item) => item.value === value)?.label || EMPTY_STRING}
                      onTextSearchChange={onChange}
                      onChange={handleChangeSalePerson}
                      label={t('client_form_title_salesperson')}
                      placeholder={t('client_form_title_salesperson_search')}
                      isLoading={isLoadingUser}
                    />
                  );
                }}
              />
            </Grid2>
          </Grid2>
        </Stack>
      )}

      <Stack gap={'14px'}>
        <Typography className={cx('title')}>
          {t(typeList === TypeCrmEnum.CLIENT ? 'client_form_title_business_settings' : 'client_form_title_notes')}
        </Typography>

        <Controller
          control={control}
          name={'note'}
          render={({ field: { onChange, value } }) => {
            return (
              <BaseInput
                multiline
                rows={10}
                height={'auto'}
                label={t(
                  typeList === TypeCrmEnum.CLIENT ? 'client_form_title_notes' : 'client_form_title_additional_notes'
                )}
                placeholder={t('client_form_title_additional_notes')}
                value={value}
                onChange={onChange}
                errorMessage={errors?.note?.message}
              />
            );
          }}
        />
      </Stack>
    </Stack>
  );
};

export default BusinessSettingsClientForm;
